const TMDB_API_KEY = 'cb2be0073d6e27a03ccf971ff99ab6f7';
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';
const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p/w500';

class TMDBService {
  async searchMovies(query) {
    try {
      const response = await fetch(
        `${TMDB_BASE_URL}/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`
      );
      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error searching TMDB movies:', error);
      return [];
    }
  }

  async getMovieDetails(tmdbId) {
    try {
      const [movieResponse, creditsResponse] = await Promise.all([
        fetch(`${TMDB_BASE_URL}/movie/${tmdbId}?api_key=${TMDB_API_KEY}`),
        fetch(`${TMDB_BASE_URL}/movie/${tmdbId}/credits?api_key=${TMDB_API_KEY}`)
      ]);

      const movie = await movieResponse.json();
      const credits = await creditsResponse.json();

      return {
        tmdb_id: movie.id,
        name: movie.title,
        description: movie.overview,
        release_date: movie.release_date,
        genre: movie.genres?.map(g => g.name) || [],
        rating: movie.vote_average,
        language: this.getLanguageName(movie.original_language || 'en'),
        poster: movie.poster_path ? `${TMDB_IMAGE_BASE_URL}${movie.poster_path}` : '',
        backdrop: movie.backdrop_path ? `${TMDB_IMAGE_BASE_URL}${movie.backdrop_path}` : '',
        runtime: movie.runtime,
        budget: movie.budget,
        revenue: movie.revenue,
        imdb_id: movie.imdb_id,
        cast: credits.cast?.slice(0, 10).map(actor => ({
          name: actor.name,
          character: actor.character,
          profile_path: actor.profile_path
        })) || [],
        crew: credits.crew?.filter(person => 
          ['Director', 'Producer', 'Writer'].includes(person.job)
        ).map(person => ({
          name: person.name,
          job: person.job,
          profile_path: person.profile_path
        })) || []
      };
    } catch (error) {
      console.error('Error fetching TMDB movie details:', error);
      return null;
    }
  }

  async searchShows(query) {
    try {
      const response = await fetch(
        `${TMDB_BASE_URL}/search/tv?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`
      );
      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error searching TMDB shows:', error);
      return [];
    }
  }

  async getShowDetails(tmdbId) {
    try {
      const [showResponse, creditsResponse] = await Promise.all([
        fetch(`${TMDB_BASE_URL}/tv/${tmdbId}?api_key=${TMDB_API_KEY}`),
        fetch(`${TMDB_BASE_URL}/tv/${tmdbId}/credits?api_key=${TMDB_API_KEY}`)
      ]);

      const show = await showResponse.json();
      const credits = await creditsResponse.json();

      return {
        tmdb_id: show.id,
        name: show.name,
        description: show.overview,
        first_air_date: show.first_air_date,
        last_air_date: show.last_air_date,
        genre: show.genres?.map(g => g.name) || [],
        rating: show.vote_average,
        poster: show.poster_path ? `${TMDB_IMAGE_BASE_URL}${show.poster_path}` : '',
        backdrop: show.backdrop_path ? `${TMDB_IMAGE_BASE_URL}${show.backdrop_path}` : '',
        number_of_seasons: show.number_of_seasons,
        number_of_episodes: show.number_of_episodes,
        status: show.status,
        cast: credits.cast?.slice(0, 10).map(actor => ({
          name: actor.name,
          character: actor.character,
          profile_path: actor.profile_path
        })) || [],
        crew: credits.crew?.filter(person => 
          ['Creator', 'Executive Producer', 'Producer'].includes(person.job)
        ).map(person => ({
          name: person.name,
          job: person.job,
          profile_path: person.profile_path
        })) || []
      };
    } catch (error) {
      console.error('Error fetching TMDB show details:', error);
      return null;
    }
  }

  getImageUrl(path, size = 'w500') {
    return path ? `https://image.tmdb.org/t/p/${size}${path}` : '';
  }

  async getMovieMedia(tmdbId) {
    try {
      const headers = {
        'Accept': 'application/json',
        'User-Agent': 'anonymous'
      };
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for mobile
      
      // Make sequential requests on mobile to avoid overwhelming connection
      const isMobile = window.innerWidth < 768;
      let imagesResponse, videosResponse;
      
      if (isMobile) {
        // Sequential requests for mobile
        imagesResponse = await fetch(`${TMDB_BASE_URL}/movie/${tmdbId}/images?api_key=${TMDB_API_KEY}`, { 
          headers, 
          signal: controller.signal 
        });
        videosResponse = await fetch(`${TMDB_BASE_URL}/movie/${tmdbId}/videos?api_key=${TMDB_API_KEY}`, { 
          headers, 
          signal: controller.signal 
        });
      } else {
        // Parallel requests for desktop
        [imagesResponse, videosResponse] = await Promise.all([
          fetch(`${TMDB_BASE_URL}/movie/${tmdbId}/images?api_key=${TMDB_API_KEY}`, { 
            headers, 
            signal: controller.signal 
          }),
          fetch(`${TMDB_BASE_URL}/movie/${tmdbId}/videos?api_key=${TMDB_API_KEY}`, { 
            headers, 
            signal: controller.signal 
          })
        ]);
      }
      
      clearTimeout(timeoutId);
      
      if (!imagesResponse.ok || !videosResponse.ok) {
        throw new Error(`TMDB API error: ${imagesResponse.status} ${videosResponse.status}`);
      }

      const images = await imagesResponse.json();
      const videos = await videosResponse.json();

      return {
        backdrops: images.backdrops?.slice(0, 20).map(img => ({
          file_path: img.file_path,
          url: `https://image.tmdb.org/t/p/w780${img.file_path}`,
          width: img.width,
          height: img.height
        })) || [],
        posters: images.posters?.slice(0, 15).map(img => ({
          file_path: img.file_path,
          url: `https://image.tmdb.org/t/p/w500${img.file_path}`,
          width: img.width,
          height: img.height
        })) || [],
        trailers: videos.results?.filter(video => 
          ['Trailer', 'Teaser', 'Clip', 'Featurette'].includes(video.type) && video.site === 'YouTube'
        ).slice(0, 10).map(video => ({
          key: video.key,
          name: video.name,
          type: video.type,
          url: `https://www.youtube.com/watch?v=${video.key}`,
          embed_url: `https://www.youtube.com/embed/${video.key}`
        })) || []
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('TMDB media request timed out after 5s');
      } else if (error.message?.includes('Failed to fetch')) {
        console.log('TMDB media request failed - network issue');
      } else {
        console.error('Error fetching TMDB movie media:', error);
      }
      return { backdrops: [], posters: [], trailers: [] };
    }
  }

  async getShowMedia(tmdbId) {
    try {
      const headers = {
        'Accept': 'application/json',
        'User-Agent': 'anonymous'
      };
      
      const [imagesResponse, videosResponse] = await Promise.all([
        fetch(`${TMDB_BASE_URL}/tv/${tmdbId}/images?api_key=${TMDB_API_KEY}`, { headers }),
        fetch(`${TMDB_BASE_URL}/tv/${tmdbId}/videos?api_key=${TMDB_API_KEY}`, { headers })
      ]);

      const images = await imagesResponse.json();
      const videos = await videosResponse.json();

      return {
        backdrops: images.backdrops?.slice(0, 10).map(img => ({
          file_path: img.file_path,
          url: `${TMDB_IMAGE_BASE_URL}${img.file_path}`,
          width: img.width,
          height: img.height
        })) || [],
        posters: images.posters?.slice(0, 10).map(img => ({
          file_path: img.file_path,
          url: `${TMDB_IMAGE_BASE_URL}${img.file_path}`,
          width: img.width,
          height: img.height
        })) || [],
        trailers: videos.results?.filter(video => 
          ['Trailer', 'Teaser', 'Clip', 'Featurette'].includes(video.type) && video.site === 'YouTube'
        ).slice(0, 10).map(video => ({
          key: video.key,
          name: video.name,
          type: video.type,
          url: `https://www.youtube.com/watch?v=${video.key}`,
          embed_url: `https://www.youtube.com/embed/${video.key}`
        })) || []
      };
    } catch (error) {
      console.error('Error fetching TMDB show media:', error);
      return { backdrops: [], posters: [], trailers: [] };
    }
  }

  // Convert ISO language code to full language name
  getLanguageName(languageCode) {
    const languageMap = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'hi': 'Hindi',
      'ar': 'Arabic',
      'te': 'Telugu',
      'ta': 'Tamil',
      'ml': 'Malayalam',
      'kn': 'Kannada',
      'bn': 'Bengali',
      'mr': 'Marathi',
      'gu': 'Gujarati',
      'pa': 'Punjabi',
      'or': 'Odia',
      'as': 'Assamese'
    };
    return languageMap[languageCode] || languageCode.toUpperCase();
  }
}

export const tmdbService = new TMDBService();